import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";

// Guard PWA service workers against iframes and Lovable preview hosts.
// The published app registers the app-shell worker via PWAUpdatePrompt so update
// prompts are handled in one place instead of registering the same worker twice.
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((regs) => {
    regs.forEach((r) => {
      const scriptUrl = r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || "";
      if (scriptUrl.endsWith("/sw.js")) r.unregister();
    });
  });
}

// Auto-recover when a stale bundle's dynamic chunk 404s after a deploy.
// Reload once per session to fetch the new entry script and chunks.
const CHUNK_RELOAD_KEY = "sisthoko_chunk_reload_at";
function isChunkLoadError(reason: unknown): boolean {
  const msg = (reason as any)?.message || String(reason || "");
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(msg);
}
function maybeReloadOnce() {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < 30_000) return; // avoid loops
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch { /* ignore */ }
  window.location.reload();
}
window.addEventListener("error", (e) => {
  if (isChunkLoadError(e.error || e.message)) maybeReloadOnce();
});
window.addEventListener("unhandledrejection", (e) => {
  if (isChunkLoadError(e.reason)) maybeReloadOnce();
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);
